import { useState } from 'react';
import useSWR from 'swr';
import { useAuthStore } from '../../hooks/useAuth';
import { 
    Clock, 
    FileText, 
    Image as ImageIcon,
    FileSpreadsheet,
    FileCode2,
    CheckCircle2,
    XCircle,
    ArrowRight,
    Loader2,
    Lock,
    Mail,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { POImportHistoryDetail } from './POImportHistoryDetail';

interface ImportSession {
    id: string;
    warehouseId: string;
    createdByUserId: string | null;
    sourceType: 'csv' | 'excel' | 'pasted_text' | 'text_pdf' | 'visual_pdf' | 'unknown' | null;
    sourceFileName: string | null;
    sourceFileSize: number | null;
    visualFallbackUsed: boolean;
    totalRows: number | null;
    status: 'parsed' | 'completed' | 'failed';
    createdAt: string;
    completedAt: string | null;
    createdByName?: string;
}

const getSourceIcon = (type: string | null) => {
    switch (type) {
        case 'csv': return <FileCode2 size={16} className="text-blue-500" />;
        case 'excel': return <FileSpreadsheet size={16} className="text-green-600" />;
        case 'text_pdf': return <FileText size={16} className="text-red-500" />;
        case 'visual_pdf': return <ImageIcon size={16} className="text-purple-500" />;
        case 'pasted_text': return <FileText size={16} className="text-slate-500" />;
        default: return <FileText size={16} className="text-slate-400" />;
    }
};

const getSourceLabel = (type: string | null) => {
    switch (type) {
        case 'csv': return 'CSV File';
        case 'excel': return 'Excel File';
        case 'text_pdf': return 'Text PDF';
        case 'visual_pdf': return 'Visual PDF (AI)';
        case 'pasted_text': return 'Pasted Text';
        default: return 'Unknown Source';
    }
};

export function POImportHistory() {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

    const { data: sessions, error, isLoading } = useSWR<ImportSession[]>(
        isOperationsEntitled && token ? ['/api/warehouse-pos/import/history', token] : null,
        async ([url, t]) => {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${t}` }
            });
            if (!res.ok) throw new Error('Failed to load history');
            return res.json();
        }
    );

    if (!isOperationsEntitled) {
        return (
            <div
                data-testid="po-import-history-operations-gate"
                className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-center max-w-2xl mx-auto space-y-4"
            >
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
                    <Lock className="w-6 h-6 text-amber-500" />
                </div>
                <div className="space-y-1.5">
                    <span className="inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">
                        Operations Plan Feature
                    </span>
                    <h3 className="text-lg font-bold text-slate-900">
                        PO Import Session History & Audit Trail Requires Operations ($199/mo)
                    </h3>
                    <p className="text-sm text-slate-600 max-w-xl mx-auto">
                        Your Starter plan ($99 USD/month per facility) includes manual PO management and CSV (<code className="font-mono">.csv</code>) PO imports. Upgrade to the Operations plan ($199 USD/month per facility) to unlock Excel (<code className="font-mono">.xlsx</code>) bulk PO imports and full Import Session History & row-level audit logs.
                    </p>
                </div>
                <div className="pt-2">
                    <a
                        href="mailto:support@baywhiz.com?subject=Upgrade%20to%20BayWhiz%20Operations%20Plan"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs transition-colors"
                    >
                        <Mail className="w-4 h-4" />
                        Contact support@baywhiz.com to Upgrade
                    </a>
                </div>
            </div>
        );
    }

    if (selectedSessionId) {
        return (
            <POImportHistoryDetail 
                sessionId={selectedSessionId} 
                onBack={() => setSelectedSessionId(null)} 
            />
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={32} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-8 bg-red-50 text-red-600 rounded-xl">
                Failed to load import history.
            </div>
        );
    }

    if (!sessions || sessions.length === 0) {
        return (
            <div className="text-center p-12 bg-slate-50 rounded-2xl border border-slate-200">
                <Clock size={32} className="mx-auto text-slate-400 mb-3" />
                <h3 className="text-lg font-semibold text-slate-900">No import history</h3>
                <p className="text-sm text-slate-500 mt-1">
                    Your PO import activity will appear here.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 font-semibold text-slate-700">Date</th>
                            <th className="px-4 py-3 font-semibold text-slate-700">Source</th>
                            <th className="px-4 py-3 font-semibold text-slate-700">User</th>
                            <th className="px-4 py-3 font-semibold text-slate-700">Rows</th>
                            <th className="px-4 py-3 font-semibold text-slate-700">Status</th>
                            <th className="px-4 py-3 font-semibold text-slate-700 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {sessions.map(session => (
                            <tr key={session.id} className="hover:bg-slate-50/50 transition-colors group">
                                <td className="px-4 py-3">
                                    <div className="font-medium text-slate-900">
                                        {format(new Date(session.createdAt), 'MMM d, yyyy')}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {format(new Date(session.createdAt), 'h:mm a')}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        {getSourceIcon(session.sourceType)}
                                        <div className="flex flex-col">
                                            <span className="font-medium text-slate-900">
                                                {getSourceLabel(session.sourceType)}
                                            </span>
                                            {session.sourceFileName && (
                                                <span className="text-xs text-slate-500 max-w-[150px] truncate">
                                                    {session.sourceFileName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                    {session.createdByName || 'System'}
                                </td>
                                <td className="px-4 py-3 text-slate-700 tabular-nums">
                                    {session.totalRows ?? '-'}
                                </td>
                                <td className="px-4 py-3">
                                    {session.status === 'completed' ? (
                                        <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-md text-xs font-semibold border border-green-200">
                                            <CheckCircle2 size={12} />
                                            Completed
                                        </span>
                                    ) : session.status === 'parsed' ? (
                                        <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md text-xs font-semibold border border-amber-200">
                                            <Clock size={12} />
                                            Draft
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-md text-xs font-semibold border border-red-200">
                                            <XCircle size={12} />
                                            Failed
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => setSelectedSessionId(session.id)}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                    >
                                        View
                                        <ArrowRight size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
